/**
 * Professional Media Upload Service
 *
 * Features:
 * - Resumable upload via TUS protocol (tus-js-client + Supabase Storage)
 * - Automatic retry with exponential back-off on network failure
 * - Resume from last byte on page reload / reconnect (fingerprint stored in localStorage)
 * - Parallel uploads (configurable concurrency)
 * - Real-time progress tracking per file and overall
 */

import * as tus from 'tus-js-client';
import { Capacitor } from '@capacitor/core';
import { supabase } from '../lib/supabase';
import { downloadMediaFromUrl } from '../utils/downloadMedia';

/**
 * Convert HEIC/HEIF file to JPEG before upload.
 * iOS captures photos as HEIC by default — most browsers can't render it.
 * heic2any is loaded lazily so it doesn't bloat the initial bundle.
 */
export const convertHeicToJpeg = async (file) => {
  const isHeic =
    file.type === 'image/heic' ||
    file.type === 'image/heif' ||
    file.name.toLowerCase().endsWith('.heic') ||
    file.name.toLowerCase().endsWith('.heif');

  if (!isHeic) return file;

  // WKWebView (iOS Capacitor) supports HEIC natively — no conversion needed,
  // and heic2any would be slow/wasteful on device.
  const isIosNative =
    Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
  if (isIosNative) return file;

  try {
    const heic2any = (await import('heic2any')).default;
    const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
    const blob = Array.isArray(converted) ? converted[0] : converted;
    const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
    return new File([blob], newName, { type: 'image/jpeg' });
  } catch (err) {
    console.warn('HEIC conversion failed, uploading original:', err);
    return file;
  }
};

// localStorage key used to persist an in-progress upload session across app restarts
export const PENDING_UPLOAD_KEY = 'mashhad_pending_upload';

// Handle for the active iOS background task (keeps JS alive when app is backgrounded)
const _bgTask = { id: null };

const _startIosBgTask = async () => {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return;
  try {
    const { Media } = await import('@capacitor-community/media');
    const result = await Media.startBackgroundTask();
    _bgTask.id = result.taskId ?? -1;
  } catch {}
};

const _endIosBgTask = async () => {
  if (_bgTask.id == null || _bgTask.id < 0) return;
  const id = _bgTask.id;
  _bgTask.id = null;
  try {
    const { Media } = await import('@capacitor-community/media');
    await Media.endBackgroundTask({ taskId: id });
  } catch {}
};

// Configuration
const CONFIG = {
  CHUNK_SIZE: 6 * 1024 * 1024,            // 6 MB per TUS chunk
  MAX_PARALLEL_UPLOADS: 3,
  MAX_FILE_SIZE: 5 * 1024 * 1024 * 1024,  // 5 GB
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/webp', 'image/heic'],
  ALLOWED_VIDEO_TYPES: ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/x-matroska'],
};

/**
 * Upload queue manager for parallel uploads
 */
class UploadQueue {
  constructor(maxConcurrent = CONFIG.MAX_PARALLEL_UPLOADS) {
    this.maxConcurrent = maxConcurrent;
    this.running = 0;
    this.queue = [];
  }

  async add(task) {
    return new Promise((resolve, reject) => {
      this.queue.push({ task, resolve, reject });
      this.processQueue();
    });
  }

  async processQueue() {
    while (this.running < this.maxConcurrent && this.queue.length > 0) {
      const { task, resolve, reject } = this.queue.shift();
      this.running++;
      
      try {
        const result = await task();
        resolve(result);
      } catch (error) {
        reject(error);
      } finally {
        this.running--;
        this.processQueue();
      }
    }
  }
}

/**
 * Validate file before upload
 */
export const validateFile = (file) => {
  const errors = [];

  // Check file size
  if (file.size > CONFIG.MAX_FILE_SIZE) {
    errors.push(`الملف "${file.name}" كبير جداً (الحد الأقصى 5GB)`);
  }

  // Check file type
  const isImage = CONFIG.ALLOWED_IMAGE_TYPES.includes(file.type);
  const isVideo = CONFIG.ALLOWED_VIDEO_TYPES.includes(file.type);
  
  if (!isImage && !isVideo) {
    errors.push(`نوع الملف "${file.name}" غير مدعوم`);
  }

  return {
    valid: errors.length === 0,
    errors,
    fileType: isVideo ? 'video' : 'image'
  };
};

/**
 * Upload a single file via TUS resumable protocol.
 * - Splits file into CONFIG.CHUNK_SIZE chunks automatically.
 * - Retries each failed chunk with exponential back-off.
 * - Stores upload fingerprint in localStorage so a reload or reconnect
 *   resumes from the last successfully uploaded byte instead of restarting.
 */
export const uploadFile = (file, storagePath, onProgress) =>
  new Promise(async (resolve) => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? '';
    const endpoint = `${supabaseUrl}/storage/v1/upload/resumable`;

    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token ?? '';

    const upload = new tus.Upload(file, {
      endpoint,
      retryDelays: [0, 3000, 6000, 12000, 24000],
      chunkSize: CONFIG.CHUNK_SIZE,
      removeFingerprintOnSuccess: true,
      headers: {
        Authorization: `Bearer ${token}`,
        'x-upsert': 'true',
      },
      metadata: {
        bucketName: 'deliveries',
        objectName: storagePath,
        contentType: file.type,
        cacheControl: '3600',
      },
      uploadDataDuringCreation: true,

      onProgress(bytesUploaded, bytesTotal) {
        const percentage = bytesTotal > 0
          ? Math.round((bytesUploaded / bytesTotal) * 100)
          : 0;
        onProgress?.({ loaded: bytesUploaded, total: bytesTotal, percentage });
      },

      onSuccess() {
        onProgress?.({ loaded: file.size, total: file.size, percentage: 100 });
        resolve({ success: true, path: storagePath });
      },

      onError(error) {
        console.error('TUS upload error:', error);
        resolve({ success: false, error: error.message ?? String(error) });
      },
    });

    // Resume a previous interrupted upload if available
    const previous = await upload.findPreviousUploads();
    if (previous.length > 0) upload.resumeFromPreviousUpload(previous[0]);

    upload.start();
  });

/**
 * Upload multiple files with parallel processing.
 * On iOS native: requests a background task so uploads survive app switching.
 * Persists session metadata to localStorage so interrupted sessions can be resumed.
 */
export const uploadMultipleFiles = async (files, userId, bookingId, onFileProgress, onOverallProgress) => {
  // Persist session before starting so a crash/background kill is recoverable
  try {
    localStorage.setItem(PENDING_UPLOAD_KEY, JSON.stringify({
      bookingId,
      files: files.map(f => ({ name: f.name, size: f.size, type: f.type })),
      startedAt: new Date().toISOString(),
    }));
  } catch {}

  // Ask iOS to keep the WebView alive while uploading
  await _startIosBgTask();

  const queue = new UploadQueue(CONFIG.MAX_PARALLEL_UPLOADS);
  const results = [];
  const fileProgress = {};
  
  // Initialize progress tracking
  files.forEach((file, index) => {
    fileProgress[index] = { loaded: 0, total: file.size, percentage: 0, status: 'pending' };
  });

  const updateOverallProgress = () => {
    const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
    const loadedBytes = Object.values(fileProgress).reduce((sum, p) => sum + p.loaded, 0);
    const percentage = totalBytes > 0 ? Math.round((loadedBytes / totalBytes) * 100) : 0;
    const completed = Object.values(fileProgress).filter(p => p.status === 'completed').length;
    const failed = Object.values(fileProgress).filter(p => p.status === 'failed').length;
    
    onOverallProgress?.({
      totalFiles: files.length,
      completedFiles: completed,
      failedFiles: failed,
      totalBytes,
      loadedBytes,
      percentage
    });
  };

  // Create upload tasks
  const uploadTasks = files.map((file, index) => async () => {
    // تحويل HEIC → JPEG تلقائياً قبل الرفع
    const processedFile = await convertHeicToJpeg(file);

    const validation = validateFile(processedFile);
    if (!validation.valid) {
      fileProgress[index] = { ...fileProgress[index], status: 'failed', error: validation.errors[0] };
      updateOverallProgress();
      return { success: false, error: validation.errors[0], fileName: processedFile.name };
    }
    // استخدم الملف المُحوَّل بدل الأصلي
    file = processedFile;

    fileProgress[index].status = 'uploading';
    updateOverallProgress();

    const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const fileExt = file.name.split('.').pop();
    const storagePath = `${userId}/${bookingId}/${fileId}.${fileExt}`;

    const result = await uploadFile(file, storagePath, (progress) => {
      fileProgress[index] = { ...progress, status: 'uploading' };
      onFileProgress?.(index, file.name, progress);
      updateOverallProgress();
    });

    if (result.success) {
      fileProgress[index] = { ...fileProgress[index], status: 'completed', percentage: 100 };
    } else {
      fileProgress[index] = { ...fileProgress[index], status: 'failed', error: result.error };
    }
    updateOverallProgress();

    return {
      ...result,
      fileName: file.name,
      fileType: validation.fileType,
      fileSize: file.size,
      mimeType: file.type,
      storagePath
    };
  });

  // Execute uploads in parallel
  const uploadPromises = uploadTasks.map(task => queue.add(task));
  let uploadResults;
  try {
    uploadResults = await Promise.allSettled(uploadPromises);
  } finally {
    // Release iOS background task and clear persisted session
    await _endIosBgTask();
    try { localStorage.removeItem(PENDING_UPLOAD_KEY); } catch {}
  }

  return uploadResults.map((result, index) => {
    if (result.status === 'fulfilled') {
      return result.value;
    } else {
      return { 
        success: false, 
        error: result.reason?.message || 'Unknown error',
        fileName: files[index].name 
      };
    }
  });
};

/**
 * Generate video thumbnail (client-side)
 * Creates a thumbnail from the first frame of a video
 */
export const generateVideoThumbnail = (videoFile) => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    video.onloadedmetadata = () => {
      // Seek to 1 second or 10% of duration, whichever is smaller
      video.currentTime = Math.min(1, video.duration * 0.1);
    };

    video.onseeked = () => {
      canvas.width = 320; // Thumbnail width
      canvas.height = Math.round(320 * (video.videoHeight / video.videoWidth));
      
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(video.src);
        resolve(blob);
      }, 'image/jpeg', 0.7);
    };

    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error('Failed to load video for thumbnail'));
    };

    video.src = URL.createObjectURL(videoFile);
  });
};

/**
 * Generate image thumbnail (client-side)
 * Resizes the image to max 400px wide while preserving aspect ratio
 */
export const generateImageThumbnail = (imageFile) => {
  return new Promise((resolve) => {
    const img = new window.Image();
    const objectUrl = URL.createObjectURL(imageFile);

    img.onload = () => {
      const MAX_WIDTH = 400;
      const scale = img.width > MAX_WIDTH ? MAX_WIDTH / img.width : 1;
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(objectUrl);
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.75);
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(null);
    };

    img.src = objectUrl;
  });
};

/** Default TTL for delivery signed URLs: 7 days */
const SIGNED_URL_EXPIRY_SECONDS = 604800;

/**
 * Get signed URL for a file
 */
export const getSignedUrl = async (filePath, expiresIn = SIGNED_URL_EXPIRY_SECONDS) => {
  const { data, error } = await supabase.storage
    .from('deliveries')
    .createSignedUrl(filePath, expiresIn);

  if (error) {
    console.error('Error getting signed URL:', error);
    return null;
  }

  return data?.signedUrl;
};

/**
 * Get a thumbnail-sized signed URL for an image using Supabase Storage Transform.
 * Eliminates the need to store a separate thumbnail file for images.
 * Falls back to full-size URL if transform fails.
 */
export const getImageThumbnailUrl = async (filePath, expiresIn = SIGNED_URL_EXPIRY_SECONDS) => {
  const { data, error } = await supabase.storage
    .from('deliveries')
    .createSignedUrl(filePath, expiresIn, {
      transform: { width: 320, quality: 75, resize: 'cover' },
    });

  if (error || !data?.signedUrl) {
    // Fallback to full URL
    return getSignedUrl(filePath, expiresIn);
  }
  return data.signedUrl;
};

/**
 * Get multiple signed URLs
 */
export const getSignedUrls = async (filePaths, expiresIn = SIGNED_URL_EXPIRY_SECONDS) => {
  const { data, error } = await supabase.storage
    .from('deliveries')
    .createSignedUrls(filePaths, expiresIn);

  if (error) {
    console.error('Error getting signed URLs:', error);
    return [];
  }

  return data || [];
};

/**
 * Download all files - uses downloadMedia for mobile compatibility (PWA/WebView)
 */
export const downloadAllFiles = async (files, onProgress) => {
  const signedUrls = await getSignedUrls(files.map(f => f.file_path));

  for (let i = 0; i < signedUrls.length; i++) {
    const urlData = signedUrls[i];
    if (urlData?.signedUrl) {
      try {
        await downloadMediaFromUrl(
          urlData.signedUrl,
          files[i].file_name,
          files[i].file_type
        );
        onProgress?.({ current: i + 1, total: files.length });
        if (i < signedUrls.length - 1) {
          await new Promise(r => setTimeout(r, 500));
        }
      } catch (error) {
        console.error(`Failed to download ${files[i].file_name}:`, error);
        onProgress?.({ current: i + 1, total: files.length, failed: true });
      }
    }
  }
};

export default {
  validateFile,
  convertHeicToJpeg,
  uploadFile,
  uploadMultipleFiles,
  generateVideoThumbnail,
  generateImageThumbnail,
  getSignedUrl,
  getImageThumbnailUrl,
  getSignedUrls,
  downloadAllFiles,
  CONFIG,
  PENDING_UPLOAD_KEY,
};
